import React, { useState, useEffect } from 'react';
import { useNotifyAlert } from 'context/NotifyAlert';
import { whatsappOficialApi } from 'api/whatsappOficialApi';
import { useQueryClient } from 'react-query';
import { getTenantId } from 'util/isAdmin';
import { CircularProgress } from '@material-ui/core';

const s = {
	wrap: {
		display: 'flex', flexDirection: 'column', gap: '20px',
	},
	field: {
		display: 'flex', flexDirection: 'column', gap: '6px',
	},
	label: {
		fontSize: '12px', fontWeight: '600', color: '#54656f',
		textTransform: 'uppercase', letterSpacing: '0.5px',
	},
	inputBox: {
		background: '#fff', border: '1px solid #d1d7db',
		borderRadius: '8px', overflow: 'hidden',
	},
	input: {
		width: '100%', boxSizing: 'border-box',
		border: 'none', outline: 'none',
		padding: '10px 12px', fontSize: '14px',
		color: '#111b21', background: 'transparent',
	},
	hint: {
		fontSize: '11px', color: '#8696a0',
	},
	banner: {
		background: '#e9f5f2', border: '1px solid #25d366',
		borderRadius: '8px', padding: '12px 16px',
		fontSize: '13px', color: '#1a5c44',
	},
	btn: ( saving ) => ( {
		width: '100%', padding: '12px',
		background: saving ? '#ccc' : '#008069',
		color: '#fff', border: 'none', borderRadius: '8px',
		fontSize: '15px', fontWeight: '600',
		cursor: saving ? 'not-allowed' : 'pointer',
		display: 'flex', alignItems: 'center',
		justifyContent: 'center', gap: '8px',
	} ),
};

// Campo de texto simples (não sensível)
const TextField = ( { label, hint, value, onChange, placeholder } ) => (
	<div style={s.field}>
		<label style={s.label}>{label}</label>
		<div style={s.inputBox}>
			<input
				type="text"
				value={value}
				onChange={onChange}
				placeholder={placeholder}
				style={s.input}
				autoComplete="off"
			/>
		</div>
		{hint && <span style={s.hint}>{hint}</span>}
	</div>
);

// Campo sensível: mostra bolinhas enquanto não estiver editando
const SecretField = ( { label, hint, placeholder, value, onChange, hasExisting } ) => {
	const [ editing, setEditing ] = useState( false );

	const handleFocus = () => setEditing( true );

	const handleBlur = () => {
		if ( value === '' && hasExisting ) {
			setEditing( false );
		}
	};

	return (
		<div style={s.field}>
			<label style={s.label}>{label}</label>
			<div style={s.inputBox}>
				{!editing && hasExisting ? (
					<input
						type="password"
						value="placeholder_bullets"
						readOnly
						onFocus={handleFocus}
						style={{ ...s.input, cursor: 'pointer', color: '#8696a0' }}
					/>
				) : (
					<input
						type="password"
						value={value}
						onChange={onChange}
						onBlur={handleBlur}
						placeholder={placeholder}
						style={s.input}
						autoComplete="new-password"
						autoFocus={editing && hasExisting}
					/>
				)}
			</div>
			{hint && <span style={s.hint}>{hint}</span>}
		</div>
	);
};

const MetaApiConfig = () => {
	const { setShowNotify } = useNotifyAlert();
	const queryClient = useQueryClient();
	const tenantId = getTenantId();

	const [ loading, setLoading ]   = useState( true );
	const [ saving, setSaving ]     = useState( false );
	const [ existing, setExisting ] = useState( false );
	const [ hasSecrets, setHasSecrets ] = useState( { accessToken: false, appSecret: false } );

	const [ form, setForm ] = useState( {
		phoneNumberId: '', wabaId: '', displayPhoneNumber: '',
		accessToken: '', appSecret: '',
	} );

	useEffect( () => {
		whatsappOficialApi.getTenantConfig()
			.then( res => {
				const data = res?.data ?? {};
				if ( data.hasMetaApi ) {
					setExisting( true );
					setHasSecrets( {
						accessToken : !!data.accessTokenMasked,
						appSecret   : !!data.appSecretMasked,
					} );
					setForm( prev => ( {
						...prev,
						phoneNumberId      : data.phoneNumberId      || '',
						wabaId             : data.wabaId             || '',
						displayPhoneNumber : data.displayPhoneNumber || '',
					} ) );
				}
			} )
			.catch( err => console.error( 'Erro ao carregar config Meta:', err ) )
			.finally( () => setLoading( false ) );
	}, [] );

	const set = key => e => {
		const value = e.target.value;
		setForm( prev => ( { ...prev, [key]: value } ) );
	};

	const handleSave = async () => {
		if ( !existing ) {
			if ( !form.phoneNumberId )      return setShowNotify( { status: 'error', text: 'Phone Number ID é obrigatório.' } );
			if ( !form.wabaId )             return setShowNotify( { status: 'error', text: 'WABA ID é obrigatório.' } );
			if ( !form.displayPhoneNumber ) return setShowNotify( { status: 'error', text: 'Número de exibição é obrigatório.' } );
			if ( !form.accessToken )        return setShowNotify( { status: 'error', text: 'Access Token é obrigatório.' } );
			if ( !form.appSecret )          return setShowNotify( { status: 'error', text: 'App Secret é obrigatório.' } );
		}

		// Na atualização, envia só os campos preenchidos
		const payload = existing
			? Object.fromEntries( Object.entries( form ).filter( ( [, v] ) => v !== '' ) )
			: { ...form };

		if ( existing && Object.keys( payload ).length === 0 ) {
			return setShowNotify( { status: 'error', text: 'Nenhum campo foi alterado.' } );
		}

		setSaving( true );
		try {
			if ( existing ) {
				await whatsappOficialApi.updateTenantConfig( tenantId, payload );
			} else {
				await whatsappOficialApi.createTenantConfig( { tenantId, ...payload } );
			}
			try {
				await whatsappOficialApi.subscribeWaba( tenantId );
			} catch ( e ) {
				console.warn( 'Falha ao inscrever webhook:', e );
			}
			setExisting( true );
			setHasSecrets( prev => ( {
				accessToken : prev.accessToken || !!form.accessToken,
				appSecret   : prev.appSecret   || !!form.appSecret,
			} ) );
			setForm( prev => ( { ...prev, accessToken: '', appSecret: '' } ) );
			queryClient.invalidateQueries( ['tenant-config'] );
			setShowNotify( { status: 'success', text: 'Configuração salva com sucesso!' } );
		} catch ( err ) {
			setShowNotify( { status: 'error', text: err?.response?.data?.error || 'Erro ao salvar configuração' } );
		} finally {
			setSaving( false );
		}
	};

	if ( loading ) {
		return (
			<div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
				<CircularProgress size={32} style={{ color: '#008069' }} />
			</div>
		);
	}

	return (
		<div style={s.wrap}>
			{existing && (
				<div style={s.banner}>
					Integração ativa. Clique em um campo para alterar o valor.
				</div>
			)}

			<TextField
				label="Phone Number ID"
				hint="Meta Business Suite → WhatsApp Manager → Números → Info da API"
				value={form.phoneNumberId}
				onChange={set( 'phoneNumberId' )}
				placeholder="Ex: 1264691806716561"
			/>

			<TextField
				label="WABA ID"
				hint="Meta Business Suite → WhatsApp Manager → Info da conta"
				value={form.wabaId}
				onChange={set( 'wabaId' )}
				placeholder="Ex: 2201791700390760"
			/>

			<TextField
				label="Número de exibição"
				value={form.displayPhoneNumber}
				onChange={set( 'displayPhoneNumber' )}
				placeholder="Ex: +55 11 99999-0000"
			/>

			<SecretField
				label="Access Token"
				hint="Meta for Developers → System Users → token com whatsapp_business_messaging"
				value={form.accessToken}
				onChange={set( 'accessToken' )}
				placeholder="System User Access Token"
				hasExisting={hasSecrets.accessToken}
			/>

			<SecretField
				label="App Secret"
				hint="Meta for Developers → seu App → Configurações → Básico → Chave Secreta"
				value={form.appSecret}
				onChange={set( 'appSecret' )}
				placeholder="App Secret"
				hasExisting={hasSecrets.appSecret}
			/>

			<button style={s.btn( saving )} onClick={handleSave} disabled={saving}>
				{saving && <CircularProgress size={16} style={{ color: '#fff' }} />}
				{saving ? 'Salvando...' : existing ? 'Atualizar configuração' : 'Salvar e ativar integração'}
			</button>
		</div>
	);
};

export default MetaApiConfig;
